import '@fontsource/open-sans';

import { createTheme } from '@mui/material/styles';

const getDesignTokens = (mode) => ({
  palette: {
    mode,
    // shadcn/ui zinc scale for dark mode
    primary:
      mode === 'light'
        ? { main: '#000000', light: '#333333', dark: '#000000', contrastText: '#FFFFFF' }
        : { main: '#e4e4e7', light: '#fafafa', dark: '#a1a1aa', contrastText: '#18181b' },
    secondary: {
      main: '#E10050',
      light: '#ED6696',
      dark: '#A0003A',
      contrastText: '#FFFFFF',
    },
    info:
      mode === 'light'
        ? { main: '#2196F3', light: '#64B5F6', dark: '#1976D2', contrastText: '#FFFFFF' }
        : { main: '#90CAF9', light: '#E3F2FD', dark: '#42A5F5', contrastText: '#09090b' },
    error: {
      light: '#E57373',
      main: '#F44336',
      dark: '#D32F2F',
      contrastText: '#FFFFFF',
    },
    warning: {
      light: '#FFB74D',
      main: '#FF9800',
      dark: '#F57C00',
      contrastText: '#000000',
    },
    success: {
      light: '#81C784',
      main: '#4CAF50',
      dark: '#388E3C',
      contrastText: '#FFFFFF',
    },
    text: {
      primary: mode === 'light' ? '#000000' : '#fafafa',
      secondary: mode === 'light' ? '#868686' : '#a1a1aa',
      disabled: mode === 'light' ? '#B3B3B3' : '#71717a',
    },
    background: {
      default: mode === 'light' ? '#FFFFFF' : '#1a1a1e',
      paper: mode === 'light' ? '#FFFFFF' : '#232327',
      subtle: mode === 'light' ? '#f8f8f8' : '#2c2c31',
    },
    divider: mode === 'light' ? '#E0E0E0' : '#27272a',
    action: {
      hover: mode === 'light' ? '#F5F5F5' : '#27272a',
      selected: mode === 'light' ? '#EBEBEB' : '#3f3f46',
      disabled: mode === 'light' ? '#c4c4c4' : '#52525b',
    },
    custom: {
      subtle: mode === 'light' ? '#f8f8f8' : '#27272a',
      border: mode === 'light' ? '#dadada' : '#27272a',
      loopLink: '#9c27b0',
    },
  },
  typography: {
    fontFamily: '"Open Sans", sans-serif',
    fontSize: 14,
    h1: {
      fontSize: '2em',
      color: mode === 'light' ? '#868686' : '#a1a1aa',
      lineHeight: '60px',
    },
    h2: {
      fontSize: '1.5em',
      color: mode === 'light' ? '#868686' : '#a1a1aa',
      lineHeight: '44px',
    },
    h3: {
      fontSize: '1.2em',
      fontWeight: 300,
      color: mode === 'light' ? '#868686' : '#a1a1aa',
      lineHeight: '33px',
    },
    h4: {
      fontSize: '1.125em',
      lineHeight: '16px',
    },
    body1: {
      fontSize: 14,
      lineHeight: '19px',
    },
    body2: {
      fontSize: 14,
      lineHeight: '19px',
      fontWeight: 300,
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundImage: 'none',
          backgroundColor: theme.palette.background.paper,
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          border: `1px solid ${theme.palette.divider}`,
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          boxShadow: 'none',
        },
        contained: ({ theme }) => ({
          color: theme.palette.primary.contrastText,
          backgroundColor: theme.palette.primary.main,
          '&:hover': {
            backgroundColor: theme.palette.primary.main,
          },
          '&:active': {
            boxShadow: 'none',
          },
        }),
        outlined: ({ theme }) => ({
          color: theme.palette.primary.main,
          borderColor: theme.palette.primary.main,
          backgroundColor: theme.palette.background.default,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
          },
          '&:active': {
            boxShadow: 'none',
            backgroundColor: theme.palette.divider,
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-disabled': {
            '& input.Mui-disabled, & textarea.Mui-disabled': {
              color: theme.palette.text.primary,
              WebkitTextFillColor: theme.palette.text.primary,
              opacity: 0.8,
            },
          },
        }),
      },
    },
    MuiTooltip: {
      defaultProps: {
        enterDelay: 1000,
      },
    },
  },
});

export const createAppTheme = (mode) => createTheme(getDesignTokens(mode));

// Default export for backward compatibility during migration
const theme = createAppTheme('light');
export default theme;
